/*
  RUMI motion + chrome measurement.

  Two jobs, both small enough to share one file that every page already needs:

  1. Publishes --rumi-chrome-h, the live height of the sticky announcement bar
     and header. The homepage hero is one screen tall minus that, so the press
     row at its foot lands above the fold on a 13" laptop rather than under it.

  2. Runs the staggered slide-up. Items opt in with [data-rumi-motion-item];
     a [data-rumi-motion] ancestor groups them so the delay ramps within a
     section and resets at the next one.

  The motion class goes on <html> before first paint, and only when this
  script is running and reduced motion is off — so a JS failure leaves the
  page fully visible rather than blank.
*/
(function () {
  var root = document.documentElement;
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Tells the inline watchdog in theme.liquid that the motion layer arrived,
     so it does not strip the class and undo the animation. */
  window.__rumiMotionBooted = true;

  /* ---------------------------------------------------------------
     1. Sticky chrome height
     --------------------------------------------------------------- */

  function measureChrome() {
    /* The distance from the top of the highest header section to the bottom of
       the lowest — not the sum of their heights. Summing missed the margins
       between the announcement bar and the header and came out 10px short,
       which put the hero 10px taller than one screen and the press row just
       under the fold again. Spanning top to bottom counts them by
       construction.

       Both edges are read in viewport coordinates and subtracted, so the
       result is the same at any scroll position. An earlier version added
       pageYOffset to reach document coordinates, which is only equivalent
       while the page is at the top: the header is sticky, so once it pins
       itself its bottom stops moving and the offset is pure error. Scroll
       2500px, let anything re-trigger a measurement — the announcement
       carousel changing slide height is enough — and --rumi-chrome-h became
       ~2559px. That is subtracted from 100vh for the mega menu's max-height,
       so the panel collapsed to zero and the Shop menu opened truncated. */
    var parts = document.querySelectorAll('.shopify-section-group-header-group');
    var top = Infinity;
    var bottom = -Infinity;

    for (var i = 0; i < parts.length; i++) {
      var rect = parts[i].getBoundingClientRect();
      if (rect.height <= 0) continue;
      if (rect.top < top) top = rect.top;
      if (rect.bottom > bottom) bottom = rect.bottom;
    }

    var total = bottom > top ? bottom - top : 0;

    if (!total) {
      var header = document.querySelector('.header-wrapper, sticky-header');
      if (header) total = header.getBoundingClientRect().height;
    }

    /* A header taller than half the screen is a measurement gone wrong, not a
       real header. Publishing it would take the mega menu's max-height with
       it, so keep the last good value instead. */
    if (total > 0 && total <= window.innerHeight / 2) {
      root.style.setProperty('--rumi-chrome-h', Math.ceil(total) + 'px');
    }
  }

  measureChrome();
  window.addEventListener('load', measureChrome);
  window.addEventListener('resize', function () {
    /* Cheap debounce: the value only matters once the resize settles. */
    clearTimeout(measureChrome._t);
    measureChrome._t = setTimeout(measureChrome, 150);
  });

  /* The bar grows when the brand face swaps in for the fallback. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measureChrome).catch(function () {});
  }

  if (window.ResizeObserver) {
    var observer = new ResizeObserver(measureChrome);
    var watched = document.querySelectorAll('.shopify-section-group-header-group');
    for (var w = 0; w < watched.length; w++) observer.observe(watched[w]);
  }

  /* ---------------------------------------------------------------
     2. Staggered slide-up
     --------------------------------------------------------------- */

  if (reduced || !window.IntersectionObserver) return;

  root.classList.add('rumi-motion');

  var STEP = 90; // ms between siblings — the "slight delay" in the designs
  var MAX_DELAY = 450; // long ramps stop feeling staggered and start feeling slow

  function boot() {
    var items = document.querySelectorAll('[data-rumi-motion-item]');
    if (!items.length) return;

    /* Delay ramps per group so a long page does not accumulate a two-second
       wait by the time it reaches the footer.

       A "converge" group is the exception. Its items each start at a
       different offset and share one destination — the cascade collapsing
       into a straight row — so they have to travel together. Staggering them
       would land the cards one at a time and lose the line entirely. */
    var groups = document.querySelectorAll('[data-rumi-motion]');
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].getAttribute('data-rumi-motion') === 'converge') continue;
      var kids = groups[g].querySelectorAll('[data-rumi-motion-item]');
      for (var k = 0; k < kids.length; k++) {
        kids[k].style.setProperty('--rumi-motion-delay', Math.min(k * STEP, MAX_DELAY) + 'ms');
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.classList.add('is-in');
          observer.unobserve(el);
          /* Drop the transition once it has played so a later reflow cannot
             re-animate an element the visitor is already reading. */
          setTimeout(function () {
            el.classList.add('is-settled');
          }, 1200);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      /* Anything already on screen at boot is shown immediately — animating
         the hero area after the fact reads as a glitch, not an entrance. */
      var box = item.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.9 && box.bottom > 0) {
        item.classList.add('is-in');
        continue;
      }
      observer.observe(item);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Sections re-rendered by the theme editor need their new items observed. */
  document.addEventListener('shopify:section:load', boot);
})();
